/**
 * Is this image animated? — the browser twin of ironman's `AnimatedImageDetector`
 * (`processors/helpers/AnimatedImageDetector.kt:21-130`).
 *
 * WHY THE BROWSER NEEDS ITS OWN. ironman answers this at upload time to choose a rendition
 * LANE: an animated source's `preview` is a 540p MP4 and a still one's is a 1280 px JPEG.
 * The browser answers it to choose the CLIP KIND -- §6.3: "animated images are VIDEO clips
 * without audio" -- and to decide whether the local file can be previewed directly (§5.0:
 * animated formats the element will not seek go through the proxy). If the two disagreed,
 * the editor would ask for a preview of one shape and be handed the other.
 *
 * The four rules are ported as they ship, including the ones that look like details:
 *   webp        an `ANIM` chunk
 *   png/apng    an `acTL` chunk -- BOTH extensions, because an animated PNG is a valid .png
 *               and a literal .apng that is treated as static gets flattened to frame 0
 *   gif         TWO OR MORE image descriptors, walked through the block structure. A plain
 *               scan for 0x2C will not do: that byte occurs freely inside compressed pixel
 *               data (the Kotlin comment says so, and it is the difference between "some
 *               GIFs" and "all GIFs")
 *   avif/heic/heif  a TOP-LEVEL `moov` box, and only when the first box is `ftyp`. Animated
 *               ones are ISO-BMFF image sequences; stills keep everything under `meta`.
 * Anything else, and any malformed input, answers false -- the still path works for
 * everything, so `false` is the safe default in the same sense it is on the edge.
 *
 * THREE DIVERGENCES FROM THE KOTLIN, ALL DELIBERATE, ALL NAMED (the file's premise is "copied
 * rather than improved", so anything that is not a copy says so):
 *
 *   1. ORDERING, COPIED rather than improved: a `moov` box is answered `true` BEFORE its
 *      declared size is validated (`AnimatedImageDetector.kt:72-82`), so a `moov` with a
 *      corrupt size is animated here exactly as it is there. The corrupt-size guard only ever
 *      fires on a box that precedes the `moov`. Agreeing with the edge is the whole point of
 *      this file; being cleverer than it in one branch is how the two tiers drift.
 *   2. STRICTER PNG (safe direction): this checks the 8-byte PNG signature and stops at `IEND`
 *      as well as `IDAT`, where `isAPNG` seeks blindly past byte 8 and stops only at `IDAT`.
 *      Both only ever turn a `true` into a `false` on bytes that are not a PNG at all, which is
 *      the still path — the same answer the Kotlin's own catch block gives.
 *   3. UNSIGNED SIZES (safe direction): every length is read as an unsigned 32-bit value, where
 *      the Kotlin reads a signed `Int` for the PNG chunk length. A hostile length with the top
 *      bit set would seek BACKWARDS there and merely run off the end here, so this cannot loop
 *      where the edge could.
 *
 * WHERE THE TWO TIERS CAN HONESTLY DISAGREE: `ANIMATED_SNIFF_BYTES`. ironman reads the whole
 * file (`RandomAccessFile`), the browser reads the first megabyte, so a long GIF whose second
 * image descriptor sits past the window — and an animated AVIF/HEIC whose `moov` sits behind a
 * large `meta` or `mdat`, since the walk skips by DECLARED BOX SIZE and lands past the window
 * in one step — are called still here and animated there. The clip then asks for a `preview`
 * of one shape and is handed the other; the picture is still correct (the edge's rendition is
 * authoritative) but the lane is a guess. See `ANIMATED_SNIFF_BYTES` for why the window is
 * still the right trade, and P5c's QA for the shapes worth eyeing.
 */

/**
 * How much of a file to read before deciding.
 *
 * WebP and PNG put their answer in the first few hundred bytes. A GIF's second image
 * descriptor can be anywhere, and an ISO-BMFF's `moov` can sit behind a `meta`/`mdat` of any
 * size, so both may be answered "still" on the strength of the first megabyte -- deliberately:
 * reading 40 MB off a phone's disk to colour a chip is the wrong trade, and the edge (which
 * reads the whole file) plans the rendition either way, so the cost of being wrong is a lane
 * guess and never a wrong picture.
 */
export const ANIMATED_SNIFF_BYTES = 1024 * 1024;

/** The four rules' extensions, and nothing else (open question 6: no scope beyond the edge's). */
const ANIMATED_EXTENSIONS = new Set(['webp', 'png', 'apng', 'gif', 'avif', 'heic', 'heif']);

function ascii(bytes: Uint8Array, at: number, length: number): string {
    let out = '';
    for (let i = 0; i < length; i += 1) out += String.fromCharCode(bytes[at + i] ?? 0);
    return out;
}

/** Big-endian, the byte order ISO-BMFF and PNG both use. Never negative, never NaN. */
function u32be(bytes: Uint8Array, at: number): number {
    return ((bytes[at] ?? 0) * 0x1000000) + (((bytes[at + 1] ?? 0) << 16) |
        ((bytes[at + 2] ?? 0) << 8) | (bytes[at + 3] ?? 0));
}

/** Little-endian, which is the only byte order a RIFF chunk size is ever written in. */
function u32le(bytes: Uint8Array, at: number): number {
    return (bytes[at] ?? 0) + (((bytes[at + 1] ?? 0) << 8) | ((bytes[at + 2] ?? 0) << 16)) +
        ((bytes[at + 3] ?? 0) * 0x1000000);
}

/** RIFF container: walk the chunk list looking for `ANIM`. */
function isAnimatedWebp(b: Uint8Array): boolean {
    if (b.length < 16 || ascii(b, 0, 4) !== 'RIFF' || ascii(b, 8, 4) !== 'WEBP') return false;
    let at = 12;
    while (at + 8 <= b.length) {
        if (ascii(b, at, 4) === 'ANIM') return true;
        // RIFF chunk sizes are LITTLE-endian and chunks are padded to an even length. (The
        // brief read the size big-endian and kept the little-endian read as a fallback, which
        // would have stepped by a garbage distance on the first zero-length chunk.)
        const size = u32le(b, at + 4);
        at += 8 + size + (size % 2);
    }
    return false;
}

/** PNG container: walk the chunk list looking for `acTL`. */
function isAnimatedPng(b: Uint8Array): boolean {
    const SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
    if (b.length < 8) return false;
    for (let i = 0; i < 8; i += 1) if (b[i] !== SIGNATURE[i]) return false;
    let at = 8;
    while (at + 8 <= b.length) {
        const length = u32be(b, at);
        const type = ascii(b, at + 4, 4);
        if (type === 'acTL') return true;
        if (type === 'IDAT' || type === 'IEND') return false;   // acTL always precedes IDAT
        at += 12 + length;                                       // length + type + data + CRC
    }
    return false;
}

/**
 * GIF: two or more IMAGE DESCRIPTORS, found by walking the block structure.
 *
 * header + logical screen descriptor, skip the global colour table when flagged, then
 * blocks: 0x21 extension (label + length-prefixed sub-blocks), 0x2C image descriptor (count
 * it, skip its local colour table and its LZW sub-blocks), 0x3B trailer.
 */
function isAnimatedGif(b: Uint8Array): boolean {
    if (b.length < 13) return false;
    const signature = ascii(b, 0, 6);
    if (signature !== 'GIF89a' && signature !== 'GIF87a') return false;
    let at = 13;
    const packed = b[10];
    if ((packed & 0x80) !== 0) at += 3 * (1 << ((packed & 0x07) + 1));

    const skipSubBlocks = (): boolean => {
        for (;;) {
            if (at >= b.length) return false;
            const length = b[at];
            at += 1;
            if (length === 0) return true;
            at += length;
        }
    };

    let images = 0;
    while (at < b.length) {
        const marker = b[at];
        at += 1;
        if (marker === 0x3b) return false;                       // trailer
        if (marker === 0x21) {                                   // extension
            at += 1;                                             // label
            if (!skipSubBlocks()) return false;
            continue;
        }
        if (marker === 0x2c) {                                   // image descriptor
            images += 1;
            if (images >= 2) return true;
            if (at + 9 > b.length) return false;
            const localPacked = b[at + 8];
            at += 9;
            if ((localPacked & 0x80) !== 0) at += 3 * (1 << ((localPacked & 0x07) + 1));
            at += 1;                                             // LZW minimum code size
            if (!skipSubBlocks()) return false;
            continue;
        }
        return false;                                            // not a block we know
    }
    return false;
}

/** ISO-BMFF: a top-level `moov`, and only when the first box is `ftyp`. */
function isAnimatedIsoBmff(b: Uint8Array): boolean {
    let at = 0;
    let first = true;
    while (at + 8 <= b.length) {
        const size32 = u32be(b, at);
        const type = ascii(b, at + 4, 4);
        if (first) {
            if (type !== 'ftyp') return false;
            first = false;
        }
        if (type === 'moov') return true;
        let size = size32;
        if (size32 === 0) size = b.length - at;                  // extends to EOF
        else if (size32 === 1) {
            if (at + 16 > b.length) return false;
            // 64-bit largesize; the high word is zero for anything a browser will open.
            size = u32be(b, at + 8) * 4294967296 + u32be(b, at + 12);
        }
        if (size < 8) return false;                              // corrupt — never loop
        at += size;
    }
    return false;
}

/** The verdict for a buffer whose extension is known. False for anything unrecognised. */
export function isAnimatedBytes(bytes: Uint8Array, ext: string): boolean {
    const lower = (ext || '').toLowerCase();
    if (!ANIMATED_EXTENSIONS.has(lower) || bytes.length < 12) return false;
    try {
        switch (lower) {
            case 'webp': return isAnimatedWebp(bytes);
            case 'png':
            case 'apng': return isAnimatedPng(bytes);
            case 'gif': return isAnimatedGif(bytes);
            default: return isAnimatedIsoBmff(bytes);
        }
    } catch {
        return false;
    }
}

/**
 * The same verdict for a `Blob`, reading at most `ANIMATED_SNIFF_BYTES`.
 *
 * A read that fails answers `false` for the reason the edge gives for its own catch: a picked
 * file whose backing file has moved is a real state, and the still path works for everything.
 */
export async function isAnimatedImage(blob: Blob, ext: string): Promise<boolean> {
    const lower = (ext || '').toLowerCase();
    if (!ANIMATED_EXTENSIONS.has(lower)) return false;
    try {
        const slice = blob.slice(0, Math.min(blob.size, ANIMATED_SNIFF_BYTES));
        const buffer = await slice.arrayBuffer();
        return isAnimatedBytes(new Uint8Array(buffer), lower);
    } catch {
        return false;
    }
}

// ------------------------------------------------------------------ how long is it (BUG-22)

/**
 * **The ordering fix.** An animated image's length must be known BEFORE the clip it becomes is
 * given a hold, and before any total is shown.
 *
 * Review6 BUG-22: a 0.36 s GIF was placed as a 4.0 s clip because the only witness to an
 * animated image's length was ironman's probe, which lands well after the placement (an `<img>`
 * has no `duration`, so the body cannot answer either — the correction a video gets from
 * `loadedmetadata` never comes for a GIF). The export sheet quoted 0:08.0, the user committed,
 * and the composition then retimed itself to 0:04.5 with the GIF collapsed to the `MIN_CLIP_MS`
 * floor. The user was quoted a length the editor could not deliver, and the correction arrived
 * after the decision.
 *
 * The fix is not a better correction, it is not needing one: the bytes are already in the
 * browser's hand — `isAnimatedImage` reads them to answer "animated?" — and every one of these
 * three containers declares its frame timing in the same bytes it declares its frames. So the
 * same read answers "how long", and the placement can use a real number from the start.
 *
 * WHAT IT WILL NOT DO. `durationMs` is `null` — "unknown", the state the caller already handles —
 * rather than a number, whenever the bytes cannot prove the whole length:
 *   * the walk did not reach the container's end (a truncated read, or a file larger than
 *     `ANIMATED_TIMING_BYTES`): a partial sum is SHORTER than the file, which is the same
 *     defect in the other direction;
 *   * fewer than two frames: a still has no length to quote;
 *   * an extension outside the four rules, or bytes that are not that container at all.
 * A short lie is worse than an honest "not yet": the caller keeps waiting for the probe, which
 * is exactly what it does today.
 *
 * AGREEING WITH THE EDGE, which is this file's whole premise:
 *   gif   delays are centiseconds in each Graphic Control Extension. A delay under 2 cs is read
 *         as 10 cs — ffmpeg's gif demuxer (`min_delay` 2, `default_delay` 10) and every browser
 *         do this, and it is not a detail: a 0 cs delay is the common case (both fixture frames
 *         and most exported GIFs), so reading it literally would time a real GIF at 0 ms.
 *   apng  each `fcTL` carries `delay_num`/`delay_den` in seconds; `delay_den == 0` means 1/100 s
 *         (the APNG spec). No clamp: the apng demuxer applies none.
 *   webp  each `ANMF` carries its frame duration in milliseconds, little-endian, 24-bit. No
 *         clamp, for the same reason.
 */
export interface AnimatedTiming {
    /** Frames counted in the bytes read. 0 when this is not one of the animated containers. */
    frames: number;
    /** The source's whole length in ms, or `null` when these bytes cannot prove it. */
    durationMs: number | null;
}

const UNKNOWN_TIMING: AnimatedTiming = { frames: 0, durationMs: null };

/**
 * How much of a file to read to TIME it — larger than `ANIMATED_SNIFF_BYTES` on purpose.
 *
 * The sniff window is 1 MB because being wrong there costs a rendition-lane guess. Being wrong
 * here costs the user the length of their video, so the trade is different: a GIF is read whole
 * up to this bound, and anything above it keeps the old behaviour (unknown until the probe).
 * 16 MB covers every animated image the studio has seen on QA and prod by a wide margin, and is
 * one decode's worth of memory rather than a phone's.
 */
export const ANIMATED_TIMING_BYTES = 16 * 1024 * 1024;

/** Sum of the GCE delays, in ms, and whether the trailer was reached. */
function gifTiming(b: Uint8Array): AnimatedTiming {
    if (b.length < 13) return UNKNOWN_TIMING;
    const signature = ascii(b, 0, 6);
    if (signature !== 'GIF89a' && signature !== 'GIF87a') return UNKNOWN_TIMING;
    let at = 13;
    const packed = b[10];
    if ((packed & 0x80) !== 0) at += 3 * (1 << ((packed & 0x07) + 1));

    let frames = 0;
    let totalMs = 0;
    let pendingCs: number | null = null;
    const incomplete = (): AnimatedTiming => ({ frames, durationMs: null });

    const skipSubBlocks = (): boolean => {
        for (;;) {
            if (at >= b.length) return false;
            const length = b[at];
            at += 1;
            if (length === 0) return true;
            at += length;
        }
    };

    while (at < b.length) {
        const marker = b[at];
        at += 1;
        if (marker === 0x3b) {
            // The trailer: every frame is accounted for.
            return { frames, durationMs: frames >= 2 ? totalMs : null };
        }
        if (marker === 0x21) {
            if (at >= b.length) return incomplete();
            const label = b[at];
            at += 1;
            if (label === 0xf9 && at + 6 <= b.length && b[at] === 4) {
                // block size 4: packed, delay (LE cs), transparent index, then the terminator.
                pendingCs = (b[at + 2] ?? 0) | ((b[at + 3] ?? 0) << 8);
            }
            if (!skipSubBlocks()) return incomplete();
            continue;
        }
        if (marker === 0x2c) {
            frames += 1;
            const cs = pendingCs ?? 0;
            totalMs += (cs < 2 ? 10 : cs) * 10;
            pendingCs = null;
            if (at + 9 > b.length) return incomplete();
            const localPacked = b[at + 8];
            at += 9;
            if ((localPacked & 0x80) !== 0) at += 3 * (1 << ((localPacked & 0x07) + 1));
            at += 1;
            if (!skipSubBlocks()) return incomplete();
            continue;
        }
        return incomplete();                                     // not a block we know
    }
    return incomplete();                                         // ran out before the trailer
}

/** Sum of the `fcTL` delays, in ms, and whether `IEND` was reached. */
function apngTiming(b: Uint8Array): AnimatedTiming {
    const SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
    if (b.length < 8) return UNKNOWN_TIMING;
    for (let i = 0; i < 8; i += 1) if (b[i] !== SIGNATURE[i]) return UNKNOWN_TIMING;
    let at = 8;
    let frames = 0;
    let totalMs = 0;
    while (at + 8 <= b.length) {
        const length = u32be(b, at);
        const type = ascii(b, at + 4, 4);
        if (type === 'IEND') return { frames, durationMs: frames >= 2 ? Math.round(totalMs) : null };
        if (type === 'fcTL') {
            // data: sequence(4) width(4) height(4) x(4) y(4) delay_num(2) delay_den(2) ...
            if (at + 8 + 24 > b.length) break;
            const num = ((b[at + 28] ?? 0) << 8) | (b[at + 29] ?? 0);
            const den = ((b[at + 30] ?? 0) << 8) | (b[at + 31] ?? 0);
            frames += 1;
            totalMs += (num * 1000) / (den === 0 ? 100 : den);
        }
        at += 12 + length;
    }
    return { frames, durationMs: null };
}

/** Sum of the `ANMF` frame durations, in ms, and whether the RIFF chunk list ended cleanly. */
function webpTiming(b: Uint8Array): AnimatedTiming {
    if (b.length < 16 || ascii(b, 0, 4) !== 'RIFF' || ascii(b, 8, 4) !== 'WEBP') return UNKNOWN_TIMING;
    let at = 12;
    let frames = 0;
    let totalMs = 0;
    while (at + 8 <= b.length) {
        const type = ascii(b, at, 4);
        const size = u32le(b, at + 4);
        if (at + 8 + size > b.length) return { frames, durationMs: null };   // declared past the end
        if (type === 'ANMF') {
            // payload: x(3) y(3) w(3) h(3) duration(3, LE ms) flags(1)
            const p = at + 8 + 12;
            frames += 1;
            totalMs += (b[p] ?? 0) | ((b[p + 1] ?? 0) << 8) | ((b[p + 2] ?? 0) << 16);
        }
        at += 8 + size + (size % 2);
    }
    // `at` past the end means the last chunk's padding byte was trimmed; the chunks themselves
    // all fitted, so the walk is complete either way.
    return { frames, durationMs: frames >= 2 ? totalMs : null };
}

/**
 * The source's own length, read from the same bytes that answer `isAnimatedBytes`.
 *
 * Never throws and never guesses — see `AnimatedTiming` for every case that answers `null`.
 */
export function animatedTimingBytes(bytes: Uint8Array, ext: string): AnimatedTiming {
    const lower = (ext || '').toLowerCase();
    if (!ANIMATED_EXTENSIONS.has(lower) || bytes.length < 12) return UNKNOWN_TIMING;
    try {
        switch (lower) {
            case 'webp': return webpTiming(bytes);
            case 'png':
            case 'apng': return apngTiming(bytes);
            case 'gif': return gifTiming(bytes);
            // avif/heic/heif carry their timing in the `moov`'s track headers — a box walk this
            // file does not do. They stay "animated, length unknown", which is today's behaviour.
            default: return { frames: 0, durationMs: null };
        }
    } catch {
        return UNKNOWN_TIMING;
    }
}

/**
 * **Is it animated, and how long is it** — one read, one answer, before the clip is placed.
 *
 * The verdict is computed over the first `ANIMATED_SNIFF_BYTES` exactly as `isAnimatedImage`
 * computes it, so the two can never disagree about a file; the timing is computed over
 * everything read. A file larger than `ANIMATED_TIMING_BYTES` is sniffed as before and timed
 * `null`. A read that fails answers "still, unknown", the safe default this module already uses.
 */
export async function sniffAnimated(
    blob: Blob,
    ext: string,
): Promise<{ animated: boolean; frames: number; durationMs: number | null }> {
    const lower = (ext || '').toLowerCase();
    if (!ANIMATED_EXTENSIONS.has(lower)) return { animated: false, frames: 0, durationMs: null };
    try {
        const readable = Math.min(blob.size, ANIMATED_TIMING_BYTES);
        const buffer = await blob.slice(0, Math.max(readable, Math.min(blob.size, ANIMATED_SNIFF_BYTES)))
            .arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const animated = isAnimatedBytes(bytes.subarray(0, ANIMATED_SNIFF_BYTES), lower);
        if (!animated) return { animated: false, frames: 0, durationMs: null };
        // Bytes we could not read cannot be timed: the sum would be short, and a short length is
        // the defect BUG-22 is about.
        const timing = blob.size > ANIMATED_TIMING_BYTES
            ? { frames: animatedTimingBytes(bytes, lower).frames, durationMs: null }
            : animatedTimingBytes(bytes, lower);
        return { animated: true, frames: timing.frames, durationMs: timing.durationMs };
    } catch {
        return { animated: false, frames: 0, durationMs: null };
    }
}
